import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy,
} from "@angular/core";
import {
  RecordingJob,
  RecordingJobStatus,
} from "app/js/src/domain/recording-job";
import {
  RecordingSource,
  RecordingSourceType,
} from "app/js/src/domain/recording-source";
import { LiveFeedRepository } from "app/js/src/domain/live-feed-repository";
import { LiveFeedStreamingPlatform } from "app/js/src/domain/live-feed";
import moment from "moment";
import { v4 as uuid } from "uuid";
@Component({
  selector: "evrideo-add-or-edit-recording-job",
  templateUrl: "./add-or-edit-recording-job.component.html",
  styleUrls: ["./add-or-edit-recording-job.component.scss"],
})
export class AddOrEditRecordingJobComponent implements OnInit {
  @Input() recordingJob: RecordingJob;
  @Output() recordingJobChanged = new EventEmitter<RecordingJob>();
  missingNameError = false;
  startTimeError = false;
  endTimeError = false;
  recordingSourcesError = false;
  recordingSourcesNotValidIdError = undefined;
  onDrag = false;
  tempStartTime: moment.Moment;
  tempEndTime: moment.Moment;

  @ViewChild("settingsPanel") settingsPanelRef: ElementRef;
  @ViewChild("sectionGeneral") sectionGenralRef: ElementRef;
  @ViewChild("sectionSources") sectionSourcesRef: ElementRef;

  recordingSourceType = RecordingSourceType;
  platformPickerSource: RecordingSource | null = null;
  platformPickerPlatforms: LiveFeedStreamingPlatform[] = [];
  platformPickerSelection: number | undefined;
  platformPickerLoading = false;
  sourcesInitializingPlatform = new Set<RecordingSource>();
  platformsByReferenceId = new Map<string, LiveFeedStreamingPlatform[]>();

  ngOnInit(): void {
    this.storeTempTimes();
    if (this.recordingJob) {
      for (const source of this.recordingJob.recordingSources) {
        if (source.type === RecordingSourceType.LiveFeed) {
          this.fetchAndCachePlatforms(source.referenceId);
        }
      }
    }
  }
  constructor(private liveFeedRepository: LiveFeedRepository) {}

  private fetchAndCachePlatforms(
    referenceId: string,
  ): Promise<LiveFeedStreamingPlatform[]> {
    const cached = this.platformsByReferenceId.get(referenceId);
    if (cached) {
      return Promise.resolve(cached);
    }
    return this.liveFeedRepository
      .getLiveFeedInfo(referenceId)
      .then((info) => {
        this.platformsByReferenceId.set(referenceId, info.streamingPlatforms);
        return info.streamingPlatforms;
      });
  }

  getPlatformName(source: RecordingSource): string | null {
    if (source.type !== RecordingSourceType.LiveFeed) {
      return null;
    }
    const platforms = this.platformsByReferenceId.get(source.referenceId);
    if (!platforms) {
      return null;
    }
    const platform = platforms.find(
      (p) => p.id === source.streamingPlatformId,
    );
    return platform ? platform.availabilityZoneName : null;
  }

  storeTempTimes() {
    if (this.recordingJob) {
      this.tempStartTime = this.recordingJob.startTime.clone();
      this.tempEndTime = this.recordingJob.endTime.clone();
    }
  }
  saveAndCloseWindow() {
    if (this.validateFields()) {
      this.recordingJob.startTime = moment.utc(this.tempStartTime.clone());
      this.recordingJob.endTime = moment.utc(this.tempEndTime.clone());
      this.tempEndTime = this.tempStartTime = null;
      this.recordingJobChanged.emit(this.recordingJob);
    }
  }

  validateFields() {
    if (!this.recordingJob) {
      return;
    }
    if (!this.recordingJob.name) {
      this.missingNameError = true;
    }
    if (this.recordingJob.recordingSources.length === 0) {
      this.recordingSourcesError = true;
    }
    return (
      this.missingNameError === false &&
      this.validateEndTime(this.tempEndTime) &&
      this.validateStartTime(this.tempStartTime) &&
      this.recordingSourcesError === false &&
      !this.recordingSourcesNotValidIdError
    );
  }

  closeWindow() {
    this.recordingJobChanged.emit();
  }

  removeSource(source: RecordingSource) {
    if (!this.recordingJob) {
      return;
    }
    const index = this.recordingJob.recordingSources.indexOf(source);
    this.recordingJob.recordingSources.splice(index, 1);
  }

  onDrop(event) {
    this.onDrag = false;
    if (!this.recordingJob) {
      return;
    }
    const data = event.dataTransfer.getData("text/recordingSource");
    if (data) {
      const recordingSource: RecordingSource = JSON.parse(data);
      if (recordingSource) {
        recordingSource.id = uuid();
        if (
          !this.recordingJob.recordingSources.find(
            (x) => x.referenceId === recordingSource.referenceId,
          )
        ) {
          this.recordingJob.recordingSources.push(recordingSource);
          this.recordingSourcesError = false;
          if (recordingSource.type === RecordingSourceType.LiveFeed) {
            this.sourcesInitializingPlatform.add(recordingSource);
            this.fetchAndCachePlatforms(recordingSource.referenceId)
              .then((platforms) => {
                if (platforms.length > 0) {
                  recordingSource.streamingPlatformId = platforms[0].id;
                }
              })
              .finally(() => {
                this.sourcesInitializingPlatform.delete(recordingSource);
                if (this.platformPickerSource === recordingSource) {
                  this.platformPickerSelection =
                    recordingSource.streamingPlatformId;
                }
              });
          }
        }
      }
    }
  }

  onStartTimeChanged(event: moment.Moment) {
    if (!this.recordingJob) {
      return;
    }
    const oldDuration = this.tempEndTime.diff(this.tempStartTime);
    const newDuration = this.tempEndTime.diff(event);
    if (oldDuration !== newDuration) {
      const shiftDuration = event.diff(this.tempStartTime);
      this.tempEndTime.add(shiftDuration);
    }
    this.tempStartTime = event;
    this.validateStartTime(event);
    this.validateEndTime(this.tempEndTime);
  }

  onEndTimeChanged(event: moment.Moment) {
    if (!this.recordingJob) {
      return;
    }
    if (this.validateEndTime(event)) {
      this.tempEndTime = event;
    }
    this.validateStartTime(this.tempStartTime);
  }

  validateEndTime(date: moment.Moment) {
    if (!this.recordingJob) {
      return;
    }
    const state = this.tempStartTime.isBefore(date);
    this.endTimeError = !state;
    return state;
  }

  validateStartTime(date: moment.Moment) {
    if (!this.recordingJob) {
      return;
    }
    let state = this.tempEndTime.isAfter(date);
    if (this.recordingJob.jobStatus !== RecordingJobStatus.Running) {
      state = state && moment.utc().isBefore(date);
    }
    this.startTimeError = !state;
    return state;
  }
  onDragEnter() {
    this.onDrag = true;
  }
  onDragLeave() {
    this.onDrag = false;
  }
  scrollSectionIntoView(section: string) {
    let topPos;
    switch (section) {
      case "general":
        topPos = this.sectionGenralRef.nativeElement.offsetTop;
        break;
      case "sources":
        topPos = this.sectionSourcesRef.nativeElement.offsetTop;
        break;
    }
    if (topPos) {
      this.settingsPanelRef.nativeElement.scrollTop = topPos;
    }
  }

  onSourceEdited(event:  FocusEvent, source: RecordingSource) {
    const input = event.target as HTMLElement;
    if (this.validateOnlyEnglishAndNumbers(input.innerText)) {
      source.mediaUniqueIdTemplate = input.innerText;
    }
  }

  onEditSourceNameKeyDown(event: KeyboardEvent) {

    const key = event.key;
    if (!this.isControlKey(event) && !this.validateOnlyEnglishAndNumbers(key)) {
      event.preventDefault(); // Block forbidden characters
    }
  }
  validateOnlyEnglishAndNumbers(value: string): boolean {
    const validRegex = /^([a-zA-Z0-9])+$/;
    if (validRegex.test(value) ) {
      this.recordingSourcesNotValidIdError = undefined; // Clear error message if valid
      return true;
    } else {
      this.recordingSourcesNotValidIdError = 'Only English letters and numbers are allowed.';
      return false;
    }
  }

  isControlKey(event: KeyboardEvent): boolean {
    return [
      'Backspace', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Delete', 'Shift', 'Control', 'Alt', 'Escape'
    ].includes(event.key);
  }

  openPlatformPicker(source: RecordingSource) {
    this.platformPickerSource = source;
    this.platformPickerSelection = source.streamingPlatformId;
    this.platformPickerPlatforms = [];
    this.platformPickerLoading = true;
    this.fetchAndCachePlatforms(source.referenceId)
      .then((platforms) => {
        if (this.platformPickerSource === source) {
          this.platformPickerPlatforms = platforms;
        }
      })
      .finally(() => {
        if (this.platformPickerSource === source) {
          this.platformPickerLoading = false;
        }
      });
  }

  isPlatformPickerLoading(): boolean {
    return (
      this.platformPickerLoading ||
      this.sourcesInitializingPlatform.has(this.platformPickerSource)
    );
  }

  closePlatformPicker() {
    this.platformPickerSource = null;
    this.platformPickerPlatforms = [];
    this.platformPickerSelection = undefined;
    this.platformPickerLoading = false;
  }

  savePlatformPicker() {
    if (this.platformPickerSource) {
      this.platformPickerSource.streamingPlatformId =
        this.platformPickerSelection;
    }
    this.closePlatformPicker();
  }
}
